import Keycloak from "keycloak-js"

import { config } from "@/app.config"

const channel = new BroadcastChannel("TOKEN_EXCHANGE")
let logoutFromHere = false
let tokenUpdatePromise: Promise<string | undefined> | null = null
let loginInProgress = false

declare module "@vue/runtime-core" {
  export interface ComponentCustomProperties {
    $keycloak: Keycloak
  }
}

const initOptions = {
  url: config.VUE_APP_KEYCLOAK_URL || "",
  realm: "project-simple",
  clientId: "login-app"
}

const TOKEN_MIN_VALIDITY_SECONDS = 70
export const KEYCLOAK_AUTH_SYNC_KEY = "kc_auth_sync"
export const KEYCLOAK_LOGOUT_SYNC_KEY = "kc_logout_sync"
export const REAUTH_SESSION_KEY = "kc_reauth"

/**
 * The tab is losing an active session, not opening a page from scratch. The mark is kept in the
 * session storage of this tab only, survives the round trip to Keycloak and lets the router tell
 * a restored page from a manually opened url after the new login.
 */
function markReauth() {
  sessionStorage.setItem(REAUTH_SESSION_KEY, "true")
}

function redirectToLogin() {
  if (loginInProgress) return
  loginInProgress = true
  markReauth()
  $keycloak.login({ redirectUri: window.location.href })
}

/** Synchronized logout keeps the current page, so it can be restored after the new login. */
function logoutFromAnotherTab() {
  markReauth()
  $keycloak.logout({ redirectUri: window.location.href })
}

function syncAuth() {
  if ($keycloak.authenticated) {
    updateToken().catch(() => undefined)
    return
  }
  redirectToLogin()
}

function refreshTokenOnResume() {
  if (!$keycloak.authenticated) return
  updateToken().catch(() => undefined)
}

export async function updateToken(sec?: number): Promise<string | undefined> {
  if (tokenUpdatePromise) return tokenUpdatePromise

  tokenUpdatePromise = $keycloak
    .updateToken(sec ?? TOKEN_MIN_VALIDITY_SECONDS)
    .then(() => {
      return $keycloak.token
    })
    .catch(error => {
      if (!$keycloak.refreshToken || !$keycloak.authenticated) {
        channel.postMessage({ type: "auth-required" })
        redirectToLogin()
      }
      throw error
    })
    .finally(() => {
      tokenUpdatePromise = null
    })

  return tokenUpdatePromise
}

export function getToken() {
  return $keycloak.token
}

export async function logout(redirectUri?: string) {
  await $keycloak.logout({ redirectUri: redirectUri ?? window.location.origin })
  logoutFromHere = true
  localStorage.setItem(KEYCLOAK_LOGOUT_SYNC_KEY, Date.now().toString())
  setTimeout(() => {
    localStorage.removeItem(KEYCLOAK_LOGOUT_SYNC_KEY)
  }, 1000)
}

export async function forceUpdateToken() {
  return await updateToken(-1)
}

export const $keycloak = new Keycloak(initOptions)

$keycloak.onAuthRefreshError = () => {
  if (!$keycloak.refreshToken || !$keycloak.authenticated) {
    redirectToLogin()
  }
}

$keycloak.onAuthError = (errorData: any) => {
  if (
    errorData?.error === "invalid_grant" &&
    errorData?.error_description === "Incorrect redirect_uri"
  ) {
    const key = "keycloak_recovery_attempted"
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "true")
      window.location.reload()
    }
  }
}

$keycloak.onAuthSuccess = () => {
  loginInProgress = false
  sessionStorage.removeItem("keycloak_recovery_attempted")
}

channel.onmessage = event => {
  if (event.data?.type === "auth-required") {
    redirectToLogin()
  }
}

window.addEventListener("storage", function (event) {
  if (event.key === KEYCLOAK_AUTH_SYNC_KEY && event.newValue) {
    syncAuth()
  }

  if (event.key === KEYCLOAK_LOGOUT_SYNC_KEY && event.newValue) {
    logoutFromHere ? (logoutFromHere = false) : logoutFromAnotherTab()
  }
})

window.addEventListener("focus", refreshTokenOnResume)
window.addEventListener("online", refreshTokenOnResume)
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    refreshTokenOnResume()
  }
})
